<template>
    <div class="relative max-w-[176px]">
        <slot></slot>
        <div
            :class="[
                imageContainer,
                'bg-white h-30 w-[176px] overlay-container relative overflow-hidden rounded-lg mb-2 hover:cursor-pointer',
            ]"
            @click="handleViewClick"
        >
            <AssetCdnImage
                :src="previewImage"
                :class="[imageCss]"
                :height="120"
                :width="176"
                object-fit="cover"
            />
            <div class="absolute inset-0 overlay rounded-lg flex justify-center items-center z-2">
                <Btn
                    data-testid="card-dashboard-view-btn"
                    size="xs"
                    :href="link"
                    :label="$t('common:view')"
                />
            </div>
        </div>

        <div
            ref="titleRef"
            v-tooltip="{ content: cardTitle, triggers: [], shown: tooltipIndexTitle === key }"
            class="text-body-2 file-name-text font-medium truncate-text-title hover:cursor-pointer"
            @mouseover="() => showTooltip('titleRef')"
            @mouseleave="() => (tooltipIndexTitle = null)"
            @click="handleViewClick"
        >
            {{ cardTitle }}
        </div>

        <component
            :is="subtitleLink ? 'a' : 'div'"
            ref="subtitleRef"
            v-tooltip="{ content: cardSubtitle, triggers: [], shown: tooltipIndexSubtitle === key }"
            :class="[subtitleTextColor, 'subtitle-div text-body-3 truncate-text-subtitle']"
            :href="subtitleLink"
            @mouseover="() => showTooltip('subtitleRef')"
            @mouseleave="() => (tooltipIndexSubtitle = null)"
        >
            {{ cardSubtitle }}
        </component>
    </div>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance } from 'vue';
import Btn from '@components/Btn/Btn.vue';
import { isOverflown } from '@helpers/dom';

export default defineComponent({
    name: 'CardDashboard',
    components: { Btn },
    props: {
        /** name of the card */
        cardTitle: {
            type: String,
            default: null,
        },
        /** subtitle of the card */
        cardSubtitle: {
            type: String,
            default: null,
        },
        /** text color of subtitle */
        subtitleTextColor: {
            type: String,
            default: 'text-blue-600',
        },
        /** link to the card content  */
        link: {
            type: String,
            default: null,
        },
        /** if the subtitle should be a link. for example to course of flashcard or document */
        subtitleLink: {
            type: String,
            default: null,
        },
        /** use to apply tailwind classes to the image container */
        imageContainer: {
            type: String,
            default: null,
        },
        /** link to the preview image */
        previewImage: {
            type: String,
            default: null,
        },
        /** use to apply tailwind classes to the image element */
        imageCss: {
            type: String,
            default: null,
        },
    },
    emits: ['view-clicked'],
    data() {
        return {
            tooltipIndexTitle: null as string | number | symbol | null,
            tooltipIndexSubtitle: null as string | number | symbol | null,
        };
    },
    computed: {
        key() {
            return getCurrentInstance()?.vnode.key || -1;
        },
    },
    methods: {
        isOverflown,
        showTooltip(refName: string): void {
            const element = (this?.$refs[`${refName}`] as HTMLElement) ?? null;
            if (element && isOverflown(element)) {
                if (refName === 'titleRef') {
                    this.tooltipIndexTitle = this.key;
                } else {
                    this.tooltipIndexSubtitle = this.key;
                }
            }
        },
        handleViewClick(e: Event) {
            e.preventDefault();
            this.$emit('view-clicked');

            // this is needed for tracking requests
            setTimeout(() => {
                window.location.href = this.link;
            }, 300);
        },
    },
});
</script>

<style lang="scss" scoped>
.file-name-text {
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.overlay {
    opacity: 0;
}
@screen md {
    .overlay {
        background-color: rgba(0, 0, 0, 0.3);
        transition: opacity 300ms ease-in;
    }
    .overlay-container:hover {
        .overlay {
            opacity: 1;
        }
    }
}
.truncate-text-title {
    display: -webkit-box;
    max-width: 100%;
    word-break: break-word;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
    white-space: normal;
}
.truncate-text-subtitle {
    display: -webkit-box;
    max-width: 100%;
    word-break: break-word;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 1;
    white-space: normal;
}
</style>
